import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export interface Subscription {
  id: string;
  customerName: string;
  contactPersonName: string;
  emailAddress: string;
  contactTelephone: string;
  tier: string;
  status: 'active' | 'inactive' | 'pending' | 'cancelled';
  startDate: string;
  endDate: string;
  amount: number;
  autoRenew: boolean;
  userId?: string;
  billingPeriod?: string;
}

export async function getSubscriptions(): Promise<Subscription[]> {
  const res = await fetch(`${API_BASE}/subscriptions`, createAuthRequestInit());

  if (!res.ok) {
    let errorMessage = 'Failed to fetch subscriptions';
    try {
      const errorJson = await res.json();
      errorMessage = errorJson?.message || errorMessage;
    } catch {
      // ignore parse errors
    }

    if (res.status === 401) {
      errorMessage = 'Unauthorized: Please log in again.';
    } else if (res.status === 403) {
      errorMessage = 'Forbidden: Super admin access required.';
    } else if (res.status >= 500) {
      errorMessage = 'Server error: Please try again later.';
    }

    throw new Error(`${errorMessage} (Status: ${res.status})`);
  }

  const json = await res.json();
  if (json?.data?.subscriptions) return json.data.subscriptions as Subscription[];
  if (Array.isArray(json?.subscriptions)) return json.subscriptions as Subscription[];
  if (Array.isArray(json?.data)) return json.data as Subscription[];
  return [];
}

export async function createSubscription(payload: {
  subscriptionTierId?: string;
  subscriptionTierName?: string;
  startDate: string;
  endDate?: string | null;
  autoRenew?: boolean | string;
  selectedUserIds: string[];
  billingPeriod?: 'monthly' | 'annual';
}): Promise<{ created: number }> {
  const res = await fetch(
    `${API_BASE}/subscriptions`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );

  if (!res.ok) {
    let message = 'Failed to create subscription';
    try {
      const err = await res.json();
      message = err?.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(`${message} (Status: ${res.status})`);
  }

  const json = await res.json();
  return { created: json?.data?.created ?? 0 };
}

export async function deleteSubscription(id: string, opts?: { userId?: string; plan?: string }): Promise<void> {
  const res = await fetch(
    `${API_BASE}/subscriptions/${encodeURIComponent(id)}`,
    createAuthRequestInit(
      {
        method: 'DELETE',
        body: opts && (opts.userId || opts.plan) ? JSON.stringify({ userId: opts.userId, plan: opts.plan }) : undefined
      },
      Boolean(opts && (opts.userId || opts.plan))
    )
  );

  if (!res.ok && res.status !== 204) {
    let message = 'Failed to delete subscription';
    try {
      const err = await res.json();
      message = err?.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(`${message} (Status: ${res.status})`);
  }
}

export async function getSubscription(id: string): Promise<Subscription> {
  const res = await fetch(`${API_BASE}/subscriptions/${encodeURIComponent(id)}`, createAuthRequestInit());

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to fetch subscription');
  }

  const json = await res.json();
  return json.data.subscription as Subscription;
}

